import { format } from "date-fns";
import CheckCircleIcon from "@/assets/icons/check-circle.svg?react";
import InactiveStepIcon from "@/assets/icons/inactive-step.svg?react";
import {
  DELIVERED_CODE_FEDEX,
  ERROR_CODES_FEDEX,
  FedexScanEvent,
  LABEL_PRINTED_CODE_FEDEX,
  LatestStatusDetail,
  OUT_FOR_DELIVERY_CODE_FEDEX,
  PICKUP_CODE_FEDEX,
  WARNING_CODE_FEDEX,
} from "@/types/fedex.type";

const SUCESS_COLOR = "#16A34A";
const PENDING_COLOR = "#dedede";
const ERROR_COLOR = "#FF0000";
const WARNING_COLOR = "#FFA500";

type TrackingSummaryState = "SUCCESS" | "PENDING" | "ERROR";

const colors = {
  SUCCESS: SUCESS_COLOR,
  PENDING: PENDING_COLOR,
  ERROR: ERROR_COLOR,
  WARNING: WARNING_COLOR,
};

type TrackingSummaryProps = {
  scanEvents: FedexScanEvent[];
  lastScanEvent: LatestStatusDetail;
  locale: string;
};

type TrackingSummaryElemProps = {
  scanEvent?: FedexScanEvent;
  state: TrackingSummaryState;
  prevState?: TrackingSummaryState;
  nextState?: TrackingSummaryState;
  mainText: string;
  deliveryIssue: boolean;
  deliveryWarning: boolean;
  locale: string;
};

export const TrackingSummaryElem = ({
  scanEvent,
  state,
  prevState,
  nextState,
  mainText,
  deliveryIssue,
  deliveryWarning,
  locale,
}: TrackingSummaryElemProps) => {
  const nextColor = () => {
    if (nextState === "ERROR") return colors.ERROR;
    if (nextState === "PENDING") return colors.PENDING;
    if (deliveryWarning) return colors.WARNING;
    if (deliveryIssue) return colors.ERROR;
    if (nextState === "SUCCESS") return colors.SUCCESS;
    return colors.PENDING;
  };

  const color = () => {
    if (state === "ERROR") return colors.ERROR;
    if (state === "PENDING") return colors.PENDING;
    if (deliveryWarning) return colors.WARNING;
    if (deliveryIssue) return colors.ERROR;
    if (state === "SUCCESS") return colors.SUCCESS;
    return colors.PENDING;
  };

  return (
    <>
      <div className="hidden flex-1 flex-col items-center gap-0 lg:flex">
        <div className="flex w-full flex-row items-center">
          <div
            className={`w-full ${prevState ? "border-t-2" : "border-t-0"} `}
            style={{ borderColor: color() }}
          />
          <div className="w-fit">
            {state === "SUCCESS" || state === "ERROR" ? (
              <CheckCircleIcon color={color()} width={24} height={24} />
            ) : (
              <InactiveStepIcon color={color()} width={24} height={24} />
            )}
          </div>
          <div
            className={`w-full ${nextState ? "border-t-2" : "border-t-0"} `}
            style={{ borderColor: nextColor() }}
          />
        </div>
        <span className="pt-3 text-center text-sm font-semibold text-gray-700">{mainText}</span>
        <span className="text-sm text-gray-600">
          {scanEvent?.scanLocation.city}
          {scanEvent?.scanLocation.city && scanEvent?.scanLocation.countryCode ? ", " : ""}
          {scanEvent?.scanLocation.countryCode}
        </span>
        <span className="text-sm text-gray-600">
          {scanEvent?.date
            ? locale === "fr"
              ? format(new Date(scanEvent.date), `dd/MM/yyyy 'à' HH:mm`)
              : format(new Date(scanEvent.date), `MM/dd/yyyy 'at' HH:mm`)
            : ""}
        </span>
      </div>
      {/* mobile */}
      <div className="flex flex-1 flex-row items-stretch gap-4 pb-4 lg:hidden">
        <div className="flex w-[24px] flex-col items-center">
          <div className="h-[24px] w-[24px]">
            {state === "SUCCESS" || state === "ERROR" ? (
              <CheckCircleIcon color={color()} width={24} height={24} />
            ) : (
              <InactiveStepIcon color={color()} width={24} height={24} />
            )}
          </div>
          <div
            className={`-mb-4 flex-1 border-r-2 border-gray-200 ${nextState ? "" : "hidden"}`}
            style={{ borderColor: nextColor() }}
          />
        </div>
        <div className="flex w-full flex-col items-start">
          <span className="text-sm font-semibold text-gray-700">{mainText}</span>
          <span className="text-sm text-gray-600">
            {scanEvent?.scanLocation.city}
            {scanEvent?.scanLocation.city && scanEvent?.scanLocation.countryCode ? ", " : ""}
            {scanEvent?.scanLocation.countryCode}
          </span>
          <span className="text-sm text-gray-600">
            {scanEvent?.date
              ? locale === "fr"
                ? format(new Date(scanEvent.date), `dd/MM/yyyy 'à' HH:mm`)
                : format(new Date(scanEvent.date), `MM/dd/yyyy 'at' HH:mm`)
              : ""}
          </span>
        </div>
      </div>
    </>
  );
};

export const TrackingSummary = ({ scanEvents, lastScanEvent, locale }: TrackingSummaryProps) => {
  const orderCreated = scanEvents.find((el) => el.eventType === LABEL_PRINTED_CODE_FEDEX);
  const pickedUp =
    scanEvents.find((el) => el.eventType === PICKUP_CODE_FEDEX) ||
    scanEvents.find((el) => el.derivedStatus === "En transit");
  const delivered = scanEvents.find((el) => el.eventType === DELIVERED_CODE_FEDEX);
  const outForDelivery =
    lastScanEvent.code === OUT_FOR_DELIVERY_CODE_FEDEX || delivered
      ? scanEvents.find((el) => el.eventType === OUT_FOR_DELIVERY_CODE_FEDEX)
      : undefined;
  const transitEvent = scanEvents.find(
    (el) => el.derivedStatus === "En transit" || el.derivedStatus === "In transit",
  );
  const deliveryIssue = ERROR_CODES_FEDEX.includes(lastScanEvent.code);
  const deliveryWarning = WARNING_CODE_FEDEX.includes(lastScanEvent.derivedCode);

  return (
    <div className="flex w-full flex-row items-center justify-center">
      <div className="flex w-full max-w-[1050px] flex-col gap-0 lg:flex-row">
        <TrackingSummaryElem
          locale={locale}
          scanEvent={orderCreated}
          state={orderCreated ? "SUCCESS" : "PENDING"}
          mainText={locale === "fr" ? "Etiquette créée" : "Shipment information sent to FedEx"}
          nextState={pickedUp !== undefined ? "SUCCESS" : "PENDING"}
          deliveryIssue={deliveryIssue}
          deliveryWarning={deliveryWarning}
        />
        <TrackingSummaryElem
          locale={locale}
          scanEvent={pickedUp}
          state={pickedUp ? "SUCCESS" : "PENDING"}
          mainText={locale === "fr" ? "Nous avons votre colis" : "We have your package"}
          prevState={orderCreated !== undefined ? "SUCCESS" : "PENDING"}
          nextState={transitEvent !== undefined ? "SUCCESS" : "PENDING"}
          deliveryIssue={deliveryIssue}
          deliveryWarning={deliveryWarning}
        />
        <TrackingSummaryElem
          locale={locale}
          scanEvent={transitEvent}
          state={transitEvent ? "SUCCESS" : deliveryIssue ? "ERROR" : "PENDING"}
          mainText={
            deliveryWarning
              ? lastScanEvent.description
              : transitEvent
                ? locale === "fr"
                  ? "En transit"
                  : "In transit"
                : lastScanEvent?.description
          }
          prevState={pickedUp !== undefined ? "SUCCESS" : "PENDING"}
          nextState={outForDelivery !== undefined ? "SUCCESS" : "PENDING"}
          deliveryIssue={deliveryIssue}
          deliveryWarning={deliveryWarning}
        />
        <TrackingSummaryElem
          locale={locale}
          scanEvent={outForDelivery}
          state={outForDelivery ? "SUCCESS" : "PENDING"}
          mainText={locale === "fr" ? "En cours de livraison" : "Out for delivery"}
          prevState={pickedUp !== undefined ? "SUCCESS" : "PENDING"}
          nextState={delivered !== undefined ? "SUCCESS" : "PENDING"}
          deliveryIssue={deliveryIssue}
          deliveryWarning={deliveryWarning}
        />
        <TrackingSummaryElem
          locale={locale}
          scanEvent={delivered}
          state={delivered ? "SUCCESS" : "PENDING"}
          mainText={locale === "fr" ? "Livré" : "Delivered"}
          prevState={outForDelivery !== undefined ? "SUCCESS" : "PENDING"}
          deliveryIssue={deliveryIssue}
          deliveryWarning={deliveryWarning}
        />
      </div>
    </div>
  );
};
